import { namespace } from '@common-config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './calendar-grid.module.scss';
import { DeleteOutlineRounded as TrashIcon } from '@mui/icons-material';
import { EventChip } from './event-chip';
import { planOccurrenceDelete } from './layout/delete-occurrence';
import { buildChipViewModel } from './layout/chip-view-model';
import { occurrenceKey } from './layout/occurrence-key';
import type { DragState } from './use-calendar-drag';
import type { CalendarLeanEvent } from './types';

interface Props {
    date: Date;
    events: CalendarLeanEvent[];
    now: number;
    drag: DragState | null;
    onClose: () => void;
    onOpenEvent: (event: CalendarLeanEvent, mouse: { x: number; y: number }) => void;
    beginDrag: (event: CalendarLeanEvent, e: React.PointerEvent<HTMLElement>) => void;
    onHoverStart?: (event: CalendarLeanEvent, rect: DOMRect) => void;
    onHoverEnd?: () => void;
    /** Delete the ONE occurrence a chip stands for. */
    onDelete?: (event: CalendarLeanEvent) => void;
}

/**
 * The dense day, opened from a month cell's summary row. A panel beside the
 * grid rather than an overlay on top of it, so the month stays visible and a
 * row can be dragged straight back onto another day (it shares the grid's drag
 * engine — `beginDrag` is the same callback the chips in the grid use).
 */
export const DayPanel: React.FC<Props> = ({
    date, events, now, drag, onClose, onOpenEvent, beginDrag, onHoverStart, onHoverEnd, onDelete,
}) => {
    const { t, i18n } = useTranslation(namespace);
    const { groups, counts } = useMemo(() => {
        const byHour = new Map<string, CalendarLeanEvent[]>();
        const tally = { sent: 0, pending: 0, failed: 0 };

        [...events]
            .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
            .forEach(event => {
                // 'unconfirmed' went out without a receipt — it belongs with sent,
                // not with trouble.
                const status = buildChipViewModel(event, now).status;
                tally[status === 'unconfirmed' ? 'sent' : status] += 1;
                const hour = moment(event.dueDate).format('HH:00');
                const list = byHour.get(hour);
                if (list) list.push(event);
                else byHour.set(hour, [event]);
            });

        return { groups: [...byHour.entries()], counts: tally };
    }, [events, now]);

    const draggingKey = drag?.active ? drag.key : null;

    return (
        <aside className={styles.panel}>
            <div className={styles.panelHead}>
                <h3>{formatInLanguage(date, i18n.language, t('calendar.grid.panelDateFormat'))}</h3>
                <p>{t('calendar.grid.sends', { count: events.length })}</p>
                <button type="button" className={styles.panelClose} onClick={onClose} aria-label={t('calendar.grid.closeDay')}>×</button>
                {/* Counts, not a colour key: the chips' colours mean IDENTITY (which
                    campaign), so a legend of coloured swatches beside them taught
                    exactly the wrong reading. State is the tick, the "!", or neither. */}
                <div className={styles.legend}>
                    <span>{t('calendar.grid.countSent', { count: counts.sent })}</span>
                    <span>{t('calendar.grid.countWaiting', { count: counts.pending })}</span>
                    {counts.failed > 0 && <span className={styles.legendBad}>{t('calendar.grid.countFailed', { count: counts.failed })}</span>}
                </div>
            </div>

            <div className={styles.panelList}>
                {groups.length === 0 && <div className={styles.panelEmpty}>{t('calendar.grid.nothingDay')}</div>}
                {groups.map(([hour, list]) => (
                    <React.Fragment key={hour}>
                        <div className={styles.hourMark}>{hour}</div>
                        {list.map(event => (
                            /* The row is where removing ONE occurrence is
                               DISCOVERED. Everywhere else on the calendar a chip
                               is a few characters wide and the gesture has to
                               carry it; here there is room for a real control,
                               so this is the surface that teaches it exists.

                               The button is a SIBLING of the chip, not a child:
                               the chip is itself `role="button"`, and nesting one
                               interactive element in another gives a screen reader
                               a button inside a button with an ambiguous name. */
                            <div className={styles.panelRow} key={occurrenceKey(event)}>
                                <EventChip
                                    event={event}
                                    now={now}
                                    size="panel"
                                    isDragging={draggingKey === occurrenceKey(event)}
                                    onOpen={onOpenEvent}
                                    onDragStart={beginDrag}
                                    onHoverStart={onHoverStart}
                                    onHoverEnd={onHoverEnd}
                                    onDelete={onDelete}
                                />
                                {onDelete && planOccurrenceDelete(event).kind !== 'none' && (
                                    <button
                                        type="button"
                                        className={styles.panelRowDelete}
                                        /* Names the send it belongs to, because in a
                                           list of near-identical rows "Delete" alone
                                           is read out identically for every one. */
                                        aria-label={t('calendar.grid.deleteSendAt', {
                                            time: moment(event.dueDate).format('HH:mm'),
                                        })}
                                        onClick={() => onDelete(event)}
                                    >
                                        <TrashIcon className={styles.panelRowDeleteIcon} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </aside>
    );
};
